import type { IFolder } from '@apis/documents';

export interface FolderNode {
  id: string;
  label: string;
  parentId: string | null;
  isSystem: boolean;
  saving: boolean; // Its id is about to change (see IFolder.saving).
  children: FolderNode[];
}

// Converts a flat list of folders (each with a parentId) into a nested tree.
// A virtual root node ("all") sits above all parentId: null folders so the
// tree always has a single entry point regardless of how many root folders exist.
// The "all" node is a presentation-only construct ("show everything") — it has
// no upstream counterpart and is never a real folder.
export function buildTree(folders: IFolder[], rootLabel: string): FolderNode {
  const root: FolderNode = { id: 'all', label: rootLabel, parentId: null, isSystem: false, saving: false, children: [] };
  const nodeMap = new Map<string, FolderNode>();

  for (const folder of folders) {
    nodeMap.set(folder.id, { id: folder.id, label: folder.name, parentId: null, isSystem: !!folder.isSystem, saving: !!folder.saving, children: [] });
  }

  for (const folder of folders) {
    const node = nodeMap.get(folder.id)!;
    if (folder.parentId === null) {
      node.parentId = 'all';
      root.children.push(node);
    } else {
      const parent = nodeMap.get(folder.parentId);
      if (parent) {
        node.parentId = folder.parentId;
        parent.children.push(node);
      } else {
        // Parent absent from the set — surface at root rather than drop silently.
        node.parentId = 'all';
        root.children.push(node);
      }
    }
  }

  return root;
}
